import Form from "react-bootstrap/Form";
import classes from "./Products.module.css";
import {
	getDocs,
	collection,
	query,
	where,
	orderBy,
	limit,
	startAfter,
	getCountFromServer,
} from "firebase/firestore";
import { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../index";
import ProductList from "./ProductList";
import ShimmerList from "./ShimmerList";
import Alert from "react-bootstrap/Alert";
import Container from "react-bootstrap/Container";
import InfiniteLoader from "../UI/InfiniteLoader";

export default function Products() {
	const [loading, setLoading] = useState(true);
	const [products, setProducts] = useState([]);
	const [search, setSearch] = useState("");
	const [error, setError] = useState(false);
	const [selectValue, setSelectValue] = useState("");

	//state for showing if product doesn't exist in the db
	const [productNotInDb, setProductNotInDb] = useState(false);

	//state for storing last loaded data from db
	const [lastLoadedData, setLastLoadedData] = useState(null);

	//state for loading more data
	const [loadingMore, setLoadingMore] = useState(false);

	//state for storing count of products on db
	const [productCountOnDb, setProductCountOnDb] = useState(null);

	//state for checking if target element has come into view, in order to trigger infinite scroll
	const targetObserver = useRef(null);

	useEffect(() => {
		const ProductDownload = async () => {
			try {
				setLoading(true);
				setProductNotInDb(false);

				// get count from db
				const getDbCountQuery = [collection(db, "items")];

				if (selectValue === "alphabetical") {
					getDbCountQuery.push(orderBy("itemName", "asc"));
				}

				if (selectValue === "price-low") {
					getDbCountQuery.push(orderBy("itemPrice", "asc"));
				}

				if (selectValue === "price-high") {
					getDbCountQuery.push(orderBy("itemPrice", "desc"));
				}

				if (!selectValue) {
					getDbCountQuery.push(orderBy("timestamp", "desc"));
					const getDbCount = await getCountFromServer(
						query.apply(null, getDbCountQuery)
					);
					const dbCount = getDbCount.data().count;
					setProductCountOnDb(dbCount);
				}

				const product_data = [];
				const query_args = [collection(db, "items")];
				if (search) {
					query_args.push(
						where("itemNameLower", ">=", search.toLowerCase()),
						where(
							"itemNameLower",
							"<=",
							search.toLowerCase() + "\uf8ff"
						),
						limit(12)
					);
				}
				if (selectValue === "alphabetical") {
					query_args.push(orderBy("itemName", "asc"));
				}

				if (selectValue === "price-low") {
					query_args.push(orderBy("itemPrice", "asc"));
				}

				if (selectValue === "price-high") {
					query_args.push(orderBy("itemPrice", "desc"));
				}

				if (!search) {
					query_args.push(orderBy("timestamp", "desc"), limit(12));
				}

				const product_query = query.apply(null, query_args);
				const loadProduct = await getDocs(product_query);

				//get last loaded data
				const lastVisible =
					loadProduct.docs[loadProduct.docs.length - 1];
				setLastLoadedData(lastVisible);

				loadProduct.forEach((data) =>
					product_data.push({
						id: data.id,
						...data.data(),
					})
				);

				setProducts(product_data);
				if (search && product_data?.length === 0 && !error) {
					setProductNotInDb(true);
				}
			} catch (error) {
				console.error(error);
				setError(true);
			} finally {
				setLoading(false);
			}
		};
		ProductDownload();
	}, [search, selectValue, error]);

	//function to load more products from db ... It's inside a useCallback so that it doesn't recreate on each render,
	//only if the dependencies change.
	const Loadmore = useCallback(async () => {
		// check if all the data from the db has been loaded
		if (products.length === productCountOnDb) {
			return;
		}

		try {
			setLoadingMore(true);
			const product_data = [];
			const query_args = [collection(db, "items")];

			if (selectValue === "alphabetical") {
				query_args.push(
					orderBy("itemName", "asc"),
					startAfter(lastLoadedData),
					limit(12)
				);
			}

			if (selectValue === "price-low") {
				query_args.push(
					orderBy("itemPrice", "asc"),
					startAfter(lastLoadedData),
					limit(12)
				);
			}

			if (selectValue === "price-high") {
				query_args.push(
					orderBy("itemPrice", "desc"),
					startAfter(lastLoadedData),
					limit(12)
				);
			}

			if (!selectValue) {
				query_args.push(
					orderBy("timestamp", "desc"),
					startAfter(lastLoadedData),
					limit(12)
				);
			}

			const loadProduct = await getDocs(query.apply(null, query_args));

			loadProduct.forEach((data) =>
				product_data.push({
					id: data.id,
					...data.data(),
				})
			);

			//get last loaded data
			const lastVisible = loadProduct.docs[loadProduct.docs.length - 1];
			setLastLoadedData(lastVisible);

			setProducts((currentProducts) => [
				...currentProducts,
				...product_data,
			]);
		} catch (error) {
			console.error(error, "line 196");
			setLoadingMore(false);
			setError(true);
		} finally {
			setLoadingMore(false);
		}
	}, [lastLoadedData, productCountOnDb, products.length, selectValue]);

	//check if user has scrolled to the bottom of the screen if so call handle scroll which will call LoadMore function
	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && !loadingMore) {
					Loadmore();
				}
			},
			{ threshold: 1 }
		);

		if (targetObserver.current) {
			observer.observe(targetObserver.current);
		}

		const currentObserver = targetObserver.current;
		return () => {
			if (currentObserver) {
				observer.unobserve(currentObserver);
			}
		};
	}, [targetObserver, Loadmore, loadingMore]);

	return (
		<>
			<div style={{ minHeight: "100vh" }}>
				<div className={classes.searchArea}>
					<Form.Control
						type="text"
						className={classes.search}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					<Form.Select
						className={classes.select}
						value={selectValue}
						onChange={(e) => setSelectValue(e.target.value)}
						style={{ textAlign: "center" }}
					>
						<option value="">Sort by</option>
						<option value={"alphabetical"}>Alphabetical</option>
						<option value={"price-low"}>Price (low-high)</option>
						<option value={"price-high"}>Price (high-low)</option>
					</Form.Select>
				</div>

				{loading && <ShimmerList cards={30} />}

				{!loading && products.length === 0 && !search && (
					<Container>
						<div
							style={{
								display: "flex",
								alignContent: "center",
								justifyContent: "center",
								width: "100%",
								marginLeft: "1rem",
								marginRight: "1rem",
							}}
						>
							<Alert variant="info">
								Sorry there was an error loading the products,
								please try again!!
							</Alert>
						</div>
					</Container>
				)}

				{!loading && !error && <ProductList data={products} />}

				{!loadingMore && error && (
					<Container>
						<div
							style={{
								display: "flex",
								alignContent: "center",
								justifyContent: "center",
								width: "100%",
								marginTop: "5%",
								marginLeft: "1rem",
								marginRight: "1rem",
							}}
						>
							<Alert>
								Sorry there was an error loading the products,
								please try agin!!
							</Alert>
						</div>
					</Container>
				)}

				{!loading && productNotInDb && (
					<Container>
						<div
							style={{
								display: "flex",
								alignContent: "center",
								justifyContent: "center",
								width: "100%",
								marginTop: "5%",
								marginLeft: "1rem",
								marginRight: "1rem",
							}}
						>
							<Alert>
								This product is unavailable at the moment,
								please try again later
							</Alert>
						</div>
					</Container>
				)}
			</div>

			{!loading && <div ref={targetObserver}></div>}

			{loadingMore && (
				<InfiniteLoader
					color={"#ccc"}
					size={15}
					loading={loadingMore}
				/>
			)}
		</>
	);
}
